import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CircularProgress,
  Chip,
  Grid,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Venta de accesorios dentro de la OT de Venta (tipo_ot=11) — mismo criterio que
// EntregaAccesoriosOT.jsx (Adición: catálogo de equipo_accesorio del servicio,
// filtrado por ciudad = sucursal del usuario, descuenta stock e inserta en
// historial_accesorio), pero acá cada fila además captura un precio de venta
// (equipoAccesorioModel.venderAccesorio, operacion='Venta'). Se muestra junto a
// VenderEquipoOT.jsx en el mismo paso (estado 1-2): a diferencia de Adición, "Finalizar
// la Venta de Equipos" sin activar salta directo a Concluido (otVentaModel.
// finalizarVentaTx) sin pasar por el estado 4 donde vive Entrega de Accesorios — así
// que si los accesorios no se venden acá, ya no hay otro paso donde hacerlo.
// Mismo layout de 2 columnas + SectionCard que VenderEquipoOT.jsx, para que "Accesorios
// Vendidos" quede apilado justo debajo de "Equipos Vendidos en esta OT".
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

const SectionCard = ({ title, icon, color = "primary", extra, children }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderRadius: 1,
      borderTop: "3px solid",
      borderTopColor: `${color}.main`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: (theme) => alpha(theme.palette[color].main, theme.palette.mode === "dark" ? 0.28 : 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
      {extra}
    </Stack>
    {children}
  </Paper>
);

// Hook con toda la lógica + las dos mitades (left/right) ya armadas como JSX, para que
// un padre pueda apilarlas junto a las de VenderEquipoOT (useVenderEquipoOT) en una
// misma columna — así "Accesorios Vendidos" queda justo debajo de "Equipos Vendidos en
// esta OT", sin depender de la altura de la tabla de la izquierda.
export const useVenderAccesoriosOT = ({ cod_ot, cod_cliente, servicio }) => {
  const { user } = useAuth();
  const [accesorios, setAccesorios] = useState([]);
  const [vendidos, setVendidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cantidades, setCantidades] = useState({}); // { [id]: cantidad }
  const [precios, setPrecios] = useState({}); // { [id]: precioVenta }
  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errores, setErrores] = useState([]);

  const cargar = () => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      axios.get("/api/equipos-accesorio", { params: { servicio, ciudad: user.sucursal } }),
      axios.get("/api/historial-accesorio", { params: { cod_ot } }),
    ])
      .then(([resAccesorios, resHistorial]) => {
        setAccesorios(resAccesorios.data || []);
        setVendidos(resHistorial.data || []);
      })
      .catch((err) => console.error("Error al cargar accesorios", err))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [cod_ot, servicio, user?.sucursal]);

  const setCantidad = (id, valor) => {
    setCantidades((prev) => ({ ...prev, [id]: valor }));
  };
  const setPrecio = (id, valor) => {
    setPrecios((prev) => ({ ...prev, [id]: valor }));
  };

  const seleccionados = Object.entries(cantidades).filter(([, v]) => Number(v) > 0);

  const guardar = async () => {
    if (!seleccionados.length) return;
    setGuardando(true);
    setErrores([]);
    const fallidos = [];
    const exitosos = [];

    for (const [idStr, cantidadStr] of seleccionados) {
      const id = Number(idStr);
      const cantidad = Number(cantidadStr);
      const accesorio = accesorios.find((a) => a.id === id);
      if (!accesorio) continue;

      try {
        await axios.post(`/api/equipos-accesorio/${id}/vender`, {
          cantidad,
          precioVenta: Number(precios[idStr]) || 0,
          cod_ot,
          asignado: cod_cliente,
          ciudad: user.sucursal,
          observacion: "Venta de Accesorios al servicio",
          usu: user.username,
        });
        exitosos.push(idStr);
      } catch (err) {
        fallidos.push(err?.response?.data?.error || `Error al vender "${accesorio.modelo}"`);
      }
    }

    setCantidades((prev) => {
      const next = { ...prev };
      exitosos.forEach((idStr) => delete next[idStr]);
      return next;
    });
    setPrecios((prev) => {
      const next = { ...prev };
      exitosos.forEach((idStr) => delete next[idStr]);
      return next;
    });
    setErrores(fallidos);
    if (exitosos.length) setOpenSnackbar(true);
    cargar();
    setGuardando(false);
  };

  if (loading) {
    return {
      left: (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ),
      right: null,
      dialogs: null,
    };
  }

  const left = (
      <SectionCard
            title="Vender Accesorios"
            icon={<Inventory2Icon fontSize="small" />}
            color="primary"
            extra={
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={!seleccionados.length || guardando}
                onClick={guardar}
                sx={{ minHeight: 26, height: 26, fontSize: 11, ml: "auto !important" }}
              >
                {guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar Venta"}
              </Button>
            }
          >
            {!!errores.length && (
              <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
                {errores.map((msg, i) => (
                  <div key={i}>{msg}</div>
                ))}
              </MuiAlert>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, borderRadius: 0.5 }}>
              <Table size="small" stickyHeader sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Modelo</TableCell>
                    <TableCell sx={headCellSx}>Disponible (Laboratorio)</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 90 }}>Cantidad a Vender</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 100 }}>Precio de Venta</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!accesorios.length ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          No hay accesorios registrados para el servicio "{servicio}".
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    accesorios.map((a) => {
                      const stockBajo = Number(a.buen_estado) < 20;
                      return (
                        <TableRow key={a.id} hover>
                          <TableCell>{a.modelo}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={a.buen_estado}
                              color={stockBajo ? "warning" : "default"}
                              variant={stockBajo ? "filled" : "outlined"}
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              type="number"
                              value={cantidades[a.id] ?? ""}
                              onChange={(e) => setCantidad(a.id, e.target.value)}
                              inputProps={{ min: 0 }}
                              sx={{
                                width: 70,
                                "& .MuiInputBase-input": { fontSize: 11, py: 0.4 },
                                "& .MuiOutlinedInput-root": { borderRadius: "0px" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="outlined"
                              size="small"
                              type="number"
                              value={precios[a.id] ?? ""}
                              onChange={(e) => setPrecio(a.id, e.target.value)}
                              inputProps={{ min: 0, step: "0.01" }}
                              sx={{
                                width: 80,
                                "& .MuiInputBase-input": { fontSize: 11, py: 0.4 },
                                "& .MuiOutlinedInput-root": { borderRadius: "0px" },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
      </SectionCard>
  );

  const right = (
      <SectionCard
            title="Accesorios Vendidos"
            icon={<AssignmentTurnedInIcon fontSize="small" />}
            color="success"
            extra={
              <Chip size="small" color="success" label={vendidos.length} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
            }
          >
            {!vendidos.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Todavía no se vendió ningún accesorio en esta OT.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, borderRadius: 0.5 }}>
                <Table size="small" stickyHeader sx={tableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Modelo</TableCell>
                      <TableCell sx={headCellSx}>Cant.</TableCell>
                      <TableCell sx={{ ...headCellSx, width: 80 }} align="right">Precio</TableCell>
                      <TableCell sx={headCellSx}>Fecha</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendidos.map((h) => (
                      <TableRow key={h.id} hover>
                        <TableCell>{h.modelo}</TableCell>
                        <TableCell>{h.cantidad}</TableCell>
                        <TableCell align="right">{Number(h.precio_venta || 0).toFixed(2)}</TableCell>
                        <TableCell>{String(h.fecha || "").slice(0, 10)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
      </SectionCard>
  );

  const dialogs = (
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Accesorios vendidos correctamente.
        </MuiAlert>
      </Snackbar>
  );

  return { left, right, dialogs };
};

const VenderAccesoriosOT = (props) => {
  const { left, right, dialogs } = useVenderAccesoriosOT(props);
  return (
    <Box sx={{ mt: 1.5 }}>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 8 }}>{left}</Grid>
        <Grid size={{ xs: 12, md: 4 }}>{right}</Grid>
      </Grid>
      {dialogs}
    </Box>
  );
};

export default VenderAccesoriosOT;
